import type { AssistantDraft } from '@/api/schemas/assistant';

/**
 * The result of applying a draft to a form's values.
 *
 * `skippedKeys` is reported rather than swallowed: a draft that names a field the
 * form does not have is a draft for a DIFFERENT form, and the person is entitled
 * to know that part of the proposal went nowhere.
 */
export type DraftApplication = {
  /** The form's values with the draft's values applied. */
  values: Record<string, string>;
  /** The fields the draft filled, in the draft's own order. */
  appliedKeys: string[];
  /** The draft's fields this form does not have. */
  skippedKeys: string[];
};

/**
 * Applies a draft to a form's values.
 *
 * ---------------------------------------------------------------------------
 * WHY THE FORM'S OWN FIELDS DECIDE WHAT IS APPLIED
 * ---------------------------------------------------------------------------
 * The draft is keyed by the FIELD NAMES of a particular form. A draft for
 * `tenant.create` names `name` and `slug`; a draft for `catalog.product` names
 * `title` and `price`. Applying one to the other would write values into fields
 * that do not exist, or — worse — into fields that exist and mean something else.
 *
 * So the form passes its own current values, and only the fields the FORM HAS are
 * written. A draft for another form therefore applies nothing rather than
 * something wrong.
 *
 * ---------------------------------------------------------------------------
 * AN EMPTY VALUE IS A PROPOSAL TOO
 * ---------------------------------------------------------------------------
 * A draft that clears a field is applied as given. The person chose to apply the
 * draft after seeing every value in it, including the empty ones, and a rule that
 * quietly kept the old value would make the form disagree with the review the
 * person just approved.
 *
 * @param draft The published draft, or null when there is none.
 * @param fields The form's current values. Its KEYS are the form's field names.
 */
export function applyDraftToForm(draft: AssistantDraft | null, fields: object): DraftApplication {
  // The form's own values, as a flat map. The parameter is `object` rather than
  // `Record<string, string>` so a form with a NAMED state type — a page whose state is
  // `{ slug: string; displayName: string; … }` — can pass its own state without a cast.
  // The values it holds are strings either way; a form whose state is not made of strings
  // is a form whose fields are not text, and this helper has no business filling it.
  const values: Record<string, string> = { ...(fields as Record<string, string>) };
  const appliedKeys: string[] = [];
  const skippedKeys: string[] = [];

  if (draft !== null) {
    for (const [key, value] of Object.entries(draft.fields)) {
      if (!Object.hasOwn(fields, key)) {
        skippedKeys.push(key);
        continue;
      }
      values[key] = value;
      appliedKeys.push(key);
    }
  }

  return { values, appliedKeys, skippedKeys };
}
